import { useEffect, useMemo, useState } from "react";

import { FreelancerProfile } from "types";
import Loader from "components/V2/Loader/Loader";
import UserAvatarLogo from "views/application/talent/components/UserAvatarLogo";
import StarRating from "react-svg-star-rating";
import ArrowButton from "components/ArrowButton";
import { useNavigate } from "react-router-dom";
import TalentFilter from "views/application/talent/components/TalentFilter";
import { JobPosting } from "types/JobPosting";
import usePrototypeUtils from "hooks/usePrototypeUtils";
import { averageRating } from "utils/talent/utils";
import { Company } from "types/Company";
import { toast } from "react-toastify";
import MainTable from "components/V2/MainTable/MainTable";
import SkillsCell from "components/V2/MainTable/SkillsCell";
import TableWrapper from "./TableWrapper";
import profileAPI from "network/profile";

interface IFindTalent {
    job: JobPosting // Job we are matching the talents to
    company: Company // Company owner of the job
}

const FindTalent = ({ job, company }: IFindTalent) => {

    const navigate = useNavigate();
    const { getJobMatchLevel } = usePrototypeUtils();
    const [talents, setTalents] = useState<FreelancerProfile[] | undefined>();
    const [filtered, setFiltered] = useState<FreelancerProfile[]>([]);
    // Memorize the interview ratings of each talent
    const interviewRatings = useMemo(
        () => {
            const ratings = {};
            if (!talents) return ratings;
            talents.forEach(t => ratings[t.freelancer_id] = averageRating(t.User?.AssessmentInfo?.rating))
            return ratings
        }
        , [talents]
    )
    // Memorize the job level match of each talent
    const matchLevels = useMemo(
        () => {
            const levels = {};
            if (!talents || !getJobMatchLevel) return levels;
            talents.forEach(t => levels[t.freelancer_id] = getJobMatchLevel(t, job));
            return levels;
        }
        , [talents])
    // Sort the final result
    const sorted = useMemo(
        () => filtered.sort((t1, t2) => matchLevels[t2.freelancer_id].match - matchLevels[t1.freelancer_id].match)
        , [filtered, matchLevels]);


    const fetchTalents = async () => {
        try {
            setFiltered([]);
            setTalents(undefined);
            const data = await profileAPI.getTalentPaginated({ page_size: 500 });
            console.log(data);
            if (!data?.talents) throw new Error("Error while fetching talent data");
            setTalents(data.talents);
        } catch (e) {
            toast.error("Error while fetching talent data");
            console.error(e);
        }
    }
    useEffect(() => { fetchTalents() }, [job])

    const goToDetailedtTalent = async (email) => {
        navigate(`/talent/details/${email}`);
    };

    if (!talents) return <Loader />

    // Only need to display 50 at most
    const data = sorted.slice(0, 50)

    return (
        <div>
            {/* Filters */}
            <TalentFilter className="pl-[16px] py-[48px] w-[80%]" data={talents} onFilter={setFiltered} />

            {/* Talents */}
            <TableWrapper>
                <MainTable
                    heads={
                        <tr>
                            <th>Photo</th>
                            <th>Name</th>
                            <th>Role</th>
                            <th>Experience</th>
                            <th>Compensation</th>
                            <th>Skills</th>
                            <th>Interview rating</th>
                            <th>Job match %</th>
                            <th></th>
                        </tr>
                    }
                    rows={data.map(t =>
                        <tr key={t.freelancer_id}>
                            <td><UserAvatarLogo user={t.User} /></td>
                            <td>{t.User?.first_name}</td>
                            <td className="w-[200px]">{t.Role?.name}</td>
                            <td>{t.level_of_experience}</td>
                            <td>{`$${t.hourly_rate}/hour`}</td>
                            <td className="w-[210px]">
                                <SkillsCell skills={t.skills} />
                            </td>
                            <td>
                                <StarRating
                                    isReadOnly
                                    unit="float"
                                    innerRadius={30}
                                    initialRating={interviewRatings[t.freelancer_id]}
                                    containerClassName="flex justify-center mt-[18px]"
                                />
                            </td>
                            <td>{matchLevels[t.freelancer_id].string}</td>
                            <td>
                                <div className="flex w-[80px] justify-center items-center">
                                    <ArrowButton
                                        direction="NEXT"
                                        onClick={() => goToDetailedtTalent(t.User.email_id)}
                                    />
                                </div>
                            </td>
                        </tr>
                    )}
                />
            </TableWrapper>

        </div>
    );
}

export default FindTalent;